import * as React from "react";
import DatePicker = require("react-bootstrap-date-picker");
import moment = require("moment");
import { utc } from "moment/moment"
import { Col, FormGroup, ControlLabel, FormControl, Form, Alert } from "react-bootstrap"

import { appContext } from "../App/appContext"
import { norskDatoFormat, IValgLaster, IModelHandler, IModelLaster, IRouteComponent } from "../domene";

import { Switch } from "./switch"

import Models = Gf.Web.Models;
import {IAppServerProps} from "../App/appServer";

function tusenSkille(verdi: number | undefined): string | undefined {
    if (verdi === undefined) {
        return undefined;
    }
    const neg = (verdi < 0);
    const negPrefix = neg ? "-" : "";
    const abs = neg ? -verdi : verdi;
    const s = `${abs}`;
    if (s.length <= 3) {
        return negPrefix + s;
    }
    return negPrefix + tusenSkille(+s.substr(0, s.length - 3)) + " " + s.substring(s.length - 3);
}

function valgTekst(verdi: number, valg: Models.FeltValg[]): string | undefined {
    const felt = valg ? valg.filter(v => v.Verdi === verdi)[0] : undefined;
    return felt ? felt.Beskrivelse : undefined;
}

export function formatterFelt<TFeltType>(verdi: any, definisjon: Models.FeltDefinisjon<TFeltType>, laster: IValgLaster, format?: string): string | null {
    switch (definisjon.FeltDataType) {
    case Models.FeltDataType.Dato:
        return verdi ? moment(verdi).format(format || norskDatoFormat) : null;
    case Models.FeltDataType.Liste:
        if(!verdi || (typeof verdi === "string")) {
            return verdi;
        }
        const valg = laster.finnValg(definisjon.ValgKontekst);
        return valg === undefined ? null : valgTekst(verdi, valg) || null;
    case Models.FeltDataType.Boolsk:
        return verdi ? "Ja" : "Nei";
    case Models.FeltDataType.Heltall:
        return verdi;
    case Models.FeltDataType.Kroner:
        return verdi ? tusenSkille(verdi) || null : null;
    default:
        return verdi;
    }
}

export function høyrejustert<TFeltType>(felt: Models.FeltDefinisjon<TFeltType>): boolean {
    switch (felt.FeltDataType) {
    case Models.FeltDataType.Dato:
    case Models.FeltDataType.Heltall:
    case Models.FeltDataType.Kroner:
        return true;
    default:
        return false;
    }
}

export function finnFelt<TFeltType>(model: Models.EntitetModell<TFeltType> | undefined, feltType: TFeltType): Models.FeltMedVerdi<TFeltType> | undefined {
    return model === undefined ? undefined : model.Felter.filter(f => f.Definisjon.Felt === feltType)[0];
}

export function finnDefinisjon<TFeltType>(definisjoner: Models.FeltDefinisjon<TFeltType>[], feltType: TFeltType): Models.FeltDefinisjon<TFeltType> | undefined {
    return definisjoner.filter(d => d.Felt === feltType)[0];
}

export function hentFeltVerdi<TFeltType>(felt: Models.FeltMedVerdi<TFeltType> | undefined): any {
    return felt === undefined ? undefined : felt.Verdi;
}

export function feltVerdi<TFeltType>(model: Models.EntitetModell<TFeltType> | undefined, feltType: TFeltType): any {
    return hentFeltVerdi(finnFelt(model, feltType));
}

export function vertikaltSkjema<TFeltType>(model: Models.EntitetModell<TFeltType> | undefined, handler: IModelHandler<TFeltType>, ...feltTyper: TFeltType[]) {
    return <Form>
               {errors(handler)}
               {feltTyper.map(feltType => vertikaltFelt(model, handler, feltType)) }
           </Form>;
}

export function horisontaltSkjema<TFeltType>(model: Models.EntitetModell<TFeltType> | undefined, handler: IModelHandler<TFeltType>, ...feltTyper: TFeltType[]) {
    return <Form horizontal>
               {errors(handler)}
               {feltTyper.map(feltType => horisontaltFelt(model, handler, feltType))}
           </Form>;
}

function getDistinctMessages(handler: IRouteComponent) : string[] {
    const meldingHash: { [key: string]: boolean } = {};
    handler.getErrors().forEach(e => { if(e.melding) { meldingHash[e.melding] = true; } });
    return Object.keys(meldingHash).sort();
}

export function errors(handler: IRouteComponent): JSX.Element | null {
    const meldinger = getDistinctMessages(handler);
    return meldinger.length === 0 ? null : <Alert bsStyle="danger">{meldinger.map(melding => <div key={melding}>{melding}</div>)}</Alert>;
}

function beskrivelse<TFeltType>(felt: Models.FeltMedVerdi<TFeltType> | undefined): string {
    return felt ? felt.Definisjon.Beskrivelse : "";
}

export function vertikaltFelt<TFeltType>(
    model: Models.EntitetModell<TFeltType> | undefined,
    handler: IModelHandler<TFeltType>,
    feltType: TFeltType) {
    const error = handler.getError(`${feltType}`);
    const felt = finnFelt(model, feltType);
    return <FormGroup key={`${feltType}`} validationState={error ? "error" : undefined}>
               <ControlLabel>{beskrivelse(felt)}</ControlLabel>
               {feltControl(model, handler, feltType, false) }
               <FormControl.Feedback />
           </FormGroup>;
}

export function horisontaltFelt<TFeltType>(
    model: Models.EntitetModell<TFeltType> | undefined,
    handler: IModelHandler<TFeltType>,
    feltType: TFeltType): JSX.Element | null {
    const felt = finnFelt(model, feltType);
    const error = handler.getError(`${feltType}`);
    return <FormGroup key={`${feltType}`} validationState={error ? "error" : undefined}>
                      <Col componentClass={ControlLabel} sm={4}>
                          {beskrivelse(felt)}
                      </Col>
                      <Col sm={8}>
                          {feltControl(model, handler, feltType, false) }
                      </Col>
                       <FormControl.Feedback />
                  </FormGroup>;
}

export function inlineFelt<TFeltType>(
    model: Models.EntitetModell<TFeltType> | undefined,
    handler: IModelHandler<TFeltType>,
    feltType: TFeltType,
    ingenVerdiTekst?: string): JSX.Element | null {
    const error = handler.getError(`${feltType}`);
    const felt = finnFelt(model, feltType);
    const control = feltControl(model, handler, feltType, true, ingenVerdiTekst);
    return redigerbar(felt) ? <FormGroup key={`${feltType}`} validationState={error ? "error" : undefined}>{control}<FormControl.Feedback /></FormGroup> : control;
}

export function inlineFeltMedBeskrivelse<TFeltType>(
    model: Models.EntitetModell<TFeltType> | undefined,
    handler: IModelHandler<TFeltType>,
    feltType: TFeltType,
    ingenVerdiTekst?: string): JSX.Element | null {
    const error = handler.getError(`${feltType}`);
    const felt = finnFelt(model, feltType);
    const control = feltControl(model, handler, feltType, true, ingenVerdiTekst);
    const resultat = redigerbar(felt) ? <FormGroup key={`${feltType}`} validationState={error ? "error" : undefined}>{
        control}<FormControl.Feedback /></FormGroup> : control;
    return beskrivelseGui(beskrivelse(felt), resultat);
}

export function beskrivelseGui<TFeltType>(beskr: string, resultat: JSX.Element) {
    return <div className="feltMedBeskrivelse"><span className="beskrivelse">{beskr}:</span>{resultat}</div>;
}

function feltControl<TFeltType>(
    model: Models.EntitetModell<TFeltType> | undefined,
    handler: IModelHandler<TFeltType>,
    feltType: TFeltType,
    inline: boolean,
    ingenVerdiTekst?: string): JSX.Element {
    const felt = finnFelt(model, feltType);
    const verdi = hentFeltVerdi(felt);
    const indre = feltIndre(model, felt, handler, feltType, verdi, ingenVerdiTekst);
    return <div className={`felt${inline || (typeof(indre) !== "string") ? "" : " form-control"}`}>{indre}</div>;
}

function redigerbar<TFeltType>(felt: Models.FeltMedVerdi<TFeltType> | undefined): boolean {
    return appContext.redigerbar && (felt ? felt.Redigerbar : false);
}

function feltIndre<TFeltType>(
    model: Models.EntitetModell<TFeltType> | undefined,
    felt: Models.FeltMedVerdi<TFeltType> | undefined,
    handler: IModelHandler<TFeltType>,
    feltType: TFeltType,
    verdi: any,
    ingenVerdiTekst?: string): JSX.Element | string | null {
    if(felt === undefined) {
        return null;
    }
    const entitetType = (model === undefined) ? undefined : model.EntitetType;
    if (redigerbar(felt)) {
        switch (felt.Definisjon.FeltDataType) {
        case Models.FeltDataType.Tekst:
            //console.log(felt.Definisjon.Felt + " har opprinnelig verdi " + verdi);
            return <FormControl type="text" key={verdi || ""} defaultValue={verdi || ""}
                                onFocus={(evt: any) => handler.onFocus(entitetType, feltType, verdi, evt.target) }
                                onBlur={(evt: any) => handler.onBlur(felt, entitetType, feltType, verdi, evt.target.value, true, evt.target) } />;
        case Models.FeltDataType.Heltall:
            return <FormControl type="number" key={verdi || ""} defaultValue={verdi || ""}
                                onFocus={(evt: any) => handler.onFocus(entitetType, feltType, verdi, evt.target) }
                                onBlur={(evt: any) => handler.onBlur(felt, entitetType, feltType, verdi, evt.target.value, true, evt.target) } />;
        case Models.FeltDataType.Dato:
            return <DatePicker
                       dayLabels={["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"]}
                       monthLabels={["Januar", "Februar", "Mars", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Desember"]}
                       todayButtonLabel="I dag"
                       showClearButton={false}
                       dateFormat={norskDatoFormat}
                       weekStartsOnMonday={true}
                       value={verdi ? utc(verdi).toISOString() : undefined}
                       onChange={(value: string) => { handler.onBlur(felt, entitetType, feltType, verdi, value, false, null); } } />;
        case Models.FeltDataType.Liste:
            const valg = handler.laster.finnValg(felt.Definisjon.ValgKontekst);
            if (!valg) {
                return <FormControl componentClass="select"/>;
            }
            const ingenVerdiMulighet = (!felt.Påkrevd || !verdi) && valg.length > 0;
            const kunEttValg = !ingenVerdiMulighet && (valg.length === 1);
            if (kunEttValg) {
                const v = valg[0];
                const alleredeValgt = verdi === v.Verdi;
                if (alleredeValgt) {
                    return formatterFelt(verdi, felt.Definisjon, handler.laster);
                }
            }
            return <FormControl value={`${verdi}`} componentClass="select"
                             onChange={(evt: any) => handler.onBlur(felt, entitetType, feltType, verdi, (evt.target as any).value || null, false, evt.target) }>
                    {ingenVerdiMulighet ? <option key={""} value={""}>{ingenVerdiTekst || "Velg..."}</option> : null}{
                        valg.map(v => <option key={`${v.Verdi}`} value={`${v.Verdi}`}>{v.Beskrivelse}</option>)}</FormControl>;
        case Models.FeltDataType.Boolsk:
            return <Switch checked={verdi} onChange={(value: boolean) => handler.onBlur(felt, entitetType, feltType, verdi, value, false, null) } />;
        }
    }
    return formatterFelt(verdi, felt.Definisjon, handler.laster);
}

export function visSomPdf() {
    //Dette burde vel vært en del av selve handingen?
    window.location.href += (window.location.href.indexOf("?") === -1 ? "?" : "&") + "v=pdf";
}

export function feltFormattert<TFeltType>(model: Models.EntitetModell<TFeltType> | undefined, feltType: TFeltType, laster: IModelLaster, format?: string): string | null {
    const verdi = feltVerdi(model, feltType);
    return model ? formatterMedFelter(verdi, model.Felter, feltType, laster, format) : null;
}

export function feltIndex<T>(modell: Models.EntitetListeModell<T>, feltType: T) {
    return modell.Definisjoner.map(d => d.Felt).indexOf(feltType);
}

export function poliseVerdi(modell: Models.EntitetListeModell<Models.PoliseFeltType>, rad: Models.ListeRadModell, feltType: Models.PoliseFeltType): any {
    return rad.Verdier[feltIndex(modell, feltType)];
}

export function poliseVerdiFormattert(modell: Models.EntitetListeModell<Models.PoliseFeltType>, rad: Models.ListeRadModell, feltType: Models.PoliseFeltType, laster: IModelLaster): string | null {
    return formatterMedDefinisjoner(poliseVerdi(modell, rad, feltType), modell.Definisjoner, feltType, laster);
};

export function fakturaVerdi(modell: Models.EntitetListeModell<Models.FakturaFeltType>, rad: Models.ListeRadModell, feltType: Models.FakturaFeltType): any {
    return rad.Verdier[feltIndex(modell, feltType)];
}

export function fakturaVerdiFormattert(modell: Models.EntitetListeModell<Models.FakturaFeltType>, rad: Models.ListeRadModell, feltType: Models.FakturaFeltType, laster: IModelLaster): string | null {
    return formatterMedDefinisjoner(fakturaVerdi(modell, rad, feltType), modell.Definisjoner, feltType, laster);
};

function formatterMedDefinisjoner<TFeltType>(verdi: any, definisjoner: Models.FeltDefinisjon<TFeltType>[], feltType: TFeltType, laster: IModelLaster, format?: string): string | null {
    const definisjon = finnDefinisjon(definisjoner, feltType);
    return definisjon ? formatterFelt(verdi, definisjon, laster, format) : null;
}

function formatterMedFelter<TFeltType>(verdi: any, felter: Models.FeltMedVerdi<TFeltType>[], feltType: TFeltType, laster: IModelLaster, format?: string): string | null {
    const felt = felter.find(f => f.Definisjon.Felt === feltType);
    return felt ? formatterFelt(verdi, felt.Definisjon, laster, format) : null;
}

export function join(separator: JSX.Element, elems: (JSX.Element | null)[]): JSX.Element | null {
    let result: JSX.Element | null = null;
    elems.forEach(e => {
        if (e) {
            result = result ? <span>{result}{separator}{e}</span> : e;
        }
    });
    return result;
}



// WEBPACK FOOTER //
// ./Scripts/React/Component/felt.tsx